import { z } from "zod"

import { isBlank, isntNil } from "~/src/lib/any"
import { Country } from "~/src/lib/country"
import { latin } from "~/src/lib/zod-extended"

const DEFAULT_COUNTRY_CODE = "US"
const FROM_ALPHA2_NICE_ERROR = "must use the ISO two-letter country code."

// Helper to make a string field nullable and optional (accepts undefined, null, or empty string)
const nullableOptional = <T extends z.ZodString>(schema: T) =>
  latin(schema)
    .nullable()
    .optional()
    .transform((val) => (val === "" ? null : val))

// Helper for email fields that are optional (accepts undefined, null, or empty string)
// eslint-disable-next-line no-control-regex
const ANSI_STRING_REGEX = /^[\x00-\x7F\xA0-\xFF]+$/

const nullableOptionalEmail = () =>
  z
    .string()
    .nullable()
    .optional()
    .transform((val) => (val === "" || val === undefined ? null : val))
    .pipe(
      z
        .string()
        .email()
        .nullable()
        .refine((val) => val === null || ANSI_STRING_REGEX.test(val), {
          message: "Must include only latin characters (Windows-1252)",
        })
    )

const defaultStaticRowSchema = z.object({
  address_name: latin(z.string().max(35)),
  address_company: nullableOptional(z.string().max(30)),
  address_1: latin(z.string().max(35)),
  address_2: nullableOptional(z.string().max(35)),
  address_city: latin(z.string().max(30)),
  address_state: nullableOptional(z.string().max(40)),
  address_zip: latin(z.string()),
  address_country: latin(z.string()),
  address_phone: nullableOptional(z.string()),
  recipient_email: nullableOptionalEmail(),
  notification_email: nullableOptionalEmail(),
  slack: nullableOptional(z.string()),
})

const defaultRowSchema = () => {
  return defaultStaticRowSchema.superRefine((data, ctx) => {
    const { address_name, address_1, address_city, address_country, address_state, address_zip, address_phone } = data

    // Required field validations
    if (!address_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_name"],
        message: "address_name is a required field",
      })
    }

    if (!address_1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_1"],
        message: "address_1 is a required field",
      })
    }

    if (!address_city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_city"],
        message: "address_city is a required field",
      })
    }

    if (!address_zip) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_zip"],
        message: "address_zip is a required field",
      })
    }

    if (!address_country) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_country"],
        message: "address_country is a required field",
      })
    }

    // PO Box validation for non-US addresses
    if (address_country !== "US" && !isBlank(address_country) && address_1) {
      if (/P\.?O\.?[\s]*BOX/i.test(address_1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_1"],
          message: "address_1 can not be a P.O. Box when country isn't US",
        })
      }
    }

    // Country validation
    const alpha2 = isntNil(address_country) ? address_country : DEFAULT_COUNTRY_CODE
    const maybeCountry = Country.fromAlpha2(alpha2)

    if (maybeCountry instanceof Error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_country"],
        message: `address_country ${FROM_ALPHA2_NICE_ERROR}`,
      })
      return // Short-circuit if country is invalid
    }

    // State validation - required for certain countries
    if (maybeCountry.isStateRequired() && !address_state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_state"],
        message: "address_state is a required field",
      })
    }

    // State code validation
    const stateCodes = maybeCountry.stateCodes() ?? new Set<string>()
    if (stateCodes.size > 0 && address_state && !stateCodes.has(address_state)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_state"],
        message: "address_state is not a valid 2-letter state code",
      })
    }

    // Zip format validation
    if (maybeCountry.isZipRequired() && isntNil(maybeCountry.zipFormat)) {
      if (!address_zip) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_zip"],
          message: "address_zip is a required field",
        })
      } else if (!maybeCountry.zipPattern()!.test(address_zip)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_zip"],
          message: `zip formatting must be ${maybeCountry.zipFormat!.replace(/\s*,\s*/g, " or ")} in ${maybeCountry.name}`,
        })
      }
    }

    // Phone validation
    const isNonUS = address_country !== "US" && !isBlank(address_country)
    if (isNonUS && isBlank(address_phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_phone"],
        message: "phone is required when country isn't US",
      })
    }

    if (!isBlank(address_phone) && !/^\d{5,}$/.test(address_phone!.replace(/[+-\s().x]+/gi, ""))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["address_phone"],
        message: "phone must have at least 5 numbers and no special characters except for: ()+-.x",
      })
    }
  })
}

// Fields that are already defined in the base schema
const BASE_SCHEMA_FIELDS = new Set([
  "address_name",
  "address_company",
  "address_1",
  "address_2",
  "address_city",
  "address_state",
  "address_zip",
  "address_country",
  "address_phone",
  "recipient_email",
  "notification_email",
  "slack",
])

export const rowSchema = (columns: HeaderValue["columns"], formats: HeaderValue["formats"]) => {
  // Build dynamic schema based on columns, but only for fields NOT in the base schema
  const dynamicFieldValidations: Array<{
    key: string
    validator: (val: unknown) => boolean
    message: string
  }> = []

  columns.forEach(({ key: columnKey }) => {
    // Skip fields that are already defined in the base schema
    if (BASE_SCHEMA_FIELDS.has(columnKey)) {
      return
    }

    const format = formats[columnKey]
    if (format?.format === "select") {
      const legalOptionValues = format.options.map((option) => option.value)
      if (legalOptionValues.length > 0) {
        dynamicFieldValidations.push({
          key: columnKey,
          validator: (val) => typeof val === "string" && legalOptionValues.includes(val),
          message: `${columnKey} must be one of: ${legalOptionValues.join(", ")}`,
        })
      }
    } else if (format?.format) {
      // Text format fields get latin validation and are required
      // eslint-disable-next-line no-control-regex
      const ANSI_STRING_REGEX = /^[\x00-\x7F\xA0-\xFF]+$/
      dynamicFieldValidations.push({
        key: columnKey,
        validator: (val) => typeof val === "string" && val !== "" && ANSI_STRING_REGEX.test(val),
        message: `${columnKey} must be a non-empty string with latin characters`,
      })
    }
  })

  // If there are dynamic fields, add validation via superRefine
  if (dynamicFieldValidations.length > 0) {
    return defaultRowSchema()
      .passthrough()
      .superRefine((data, ctx) => {
        for (const { key, validator, message } of dynamicFieldValidations) {
          const val = (data as Record<string, unknown>)[key]
          if (!validator(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [key],
              message,
            })
          }
        }
      })
  }

  return defaultRowSchema()
}

export const DATABASE = "dropshipList"
type Stores = {
  meta: Array<string>
  headers: Array<string>
  rows: Array<string>
}
export type TableKey = keyof Stores

export type MetaValue = {
  sourceChecksum: string
  namespace: string
}

export type HeaderValue = {
  columns: Array<{ key: string; name: string }>
  formats: CustomFieldFormatObject
  namespace: string
}

export type RowValue = {
  [key: string]: unknown
  id: number
  namespace: string
}

export const STORES: Stores = {
  meta: ["&namespace"],
  headers: ["&namespace"],
  rows: ["&[namespace+id]", "namespace", "id"],
}

export type CustomFieldFormatObject = { [key: string]: CustomFieldTextFormat | CustomFieldSelectFormat }
export type CustomFieldTextFormat = { format: "text" }
export type CustomFieldSelectFormat = { format: "select"; options: Array<{ label: string; value: string }> }

// TODO: Use a zod schema to enforce this format on load
export type SourceFormat = {
  sourceChecksum: string
  columns: Array<{ name: string }>
  rows: Array<{ [key: string]: string }>
  formats: CustomFieldFormatObject
}
